<template>
    <div v-if="bulk_message">
        <v-text-field
            autocomplete="off"
            label="Search"
            prepend-icon="mdi-magnify"
            :value="full_text"
            clearable
            @input="onSearchInputDebounced"
        >
            <v-menu
                bottom
                offset-y
                slot="append-outer"
            >
                <template #activator="{ on }">
                    <v-icon v-on="on">
                        mdi-dots-vertical
                    </v-icon>
                </template>

                <v-list>
                    <v-list>
                        <v-list-item @click="$emit('update')">
                            <v-list-item-title>Refresh</v-list-item-title>
                        </v-list-item>

                        <v-divider />

                        <v-list-item @click="$emit('recipient-validation')">
                            <v-list-item-title>Validation...</v-list-item-title>
                        </v-list-item>

                        <v-list-item @click="onClickUpdateRecipientLocations">
                            <v-list-item-title>Update recipient locations...</v-list-item-title>
                        </v-list-item>

                        <m-restricted-menu-list-item
                            v-if="$rc.$state.isAuthActorSystemAdmin()"
                            text="Update number reassignment check since dates..."
                            @click="onClickUpdateNumberReassignmentCheckSinceDates"
                        />

                        <v-divider />

                        <v-list-item @click="$emit('export-recipients-to-excel')">
                            <v-list-item-title>Export to Excel...</v-list-item-title>
                        </v-list-item>

                        <v-list-item @click="$emit('view-imports')">
                            <v-list-item-title>View excel files uploaded / imported...</v-list-item-title>
                        </v-list-item>

                        <v-divider />

                        <v-list-item @click="$emit('split-message')">
                            <v-list-item-title>Split...</v-list-item-title>
                        </v-list-item>

                        <v-list-item @click="onClickCreateRecipient">
                            <v-list-item-title>Add recipient manually...</v-list-item-title>
                        </v-list-item>

                        <v-list-item @click="onClickAddContacts">
                            <v-list-item-title>Add recipients from contacts...</v-list-item-title>
                        </v-list-item>

                        <v-list-item @click="onClickExcelImport">
                            <v-list-item-title>Add recipients from an Excel file...</v-list-item-title>
                        </v-list-item>

                        <v-list-item @click="onClickAddAllListMembers">
                            <v-list-item-title>Add recipients from a list...</v-list-item-title>
                        </v-list-item>

                        <m-restricted-menu-list-item
                            v-if="$rc.$state.isAuthActorSystemAdmin() && bulk_message.is_demo_organization"
                            text="Add dummy recipients..."
                            @click="onClickAddDummyRecipients"
                        />

                        <m-restricted-menu-list-item
                            v-if="$rc.$state.isAuthActorSystemAdmin() && bulk_message.is_demo_organization"
                            text="Add dummy (spam) recipients..."
                            @click="onClickAddDummySpamRecipients"
                        />

                        <m-restricted-menu-list-item
                            v-if="$rc.$state.isAuthActorSystemAdmin() && bulk_message.is_demo_organization"
                            text="Export dummy list of recipients..."
                            @click="onClickDummyExport"
                        />

                        <m-restricted-menu-list-item
                            v-if="$rc.$state.isAuthActorSystemAdmin() && bulk_message.is_demo_organization"
                            text="Export dummy WinePulse report..."
                            @click="onClickWinePulseDummy"
                        />

                        <v-divider />

                        <v-list-item @click="$emit('edit-error-rate-before-cancel')">
                            <v-list-item-title>Adjust max error rate...</v-list-item-title>
                        </v-list-item>

                        <v-list-item @click="deleteAllRecipients">
                            <v-list-item-title>Delete all recipients...</v-list-item-title>
                        </v-list-item>
                    </v-list>
                </v-list>
            </v-menu>
        </v-text-field>

        <v-data-table
            :headers="recipient_headers"
            :items="loading_recipients ? [] : recipients"
            :items-per-page="50"
            loading-text="Loading... Please wait"
            class="elevation-1"
            :custom-sort="customRecipientSort"
            :loading="loading_recipients"
            :footer-props="{
                showFirstLastPage: true,
                itemsPerPage: 50,
                disableItemsPerPage: true,
                itemsPerPageOptions: [50, 100],
            }"
            :search="full_text"
            :options.sync="local_table_options"
            :server-items-length="num_recipients_under_current_filter"
        >
            <template #item.recipient_contact_first_name="{ item }">
                {{ prettyFirstName(item) }}
            </template>

            <template #item.recipient_contact_last_name="{ item }">
                {{ prettyLastName(item) }}
            </template>

            <template #item.recipient_endpoint="{ item }">
                {{ $rc.pretty(item.recipient_endpoint, 'endpoint_no_parens') }}
            </template>

            <template #item.locations="{ item }">
                {{ $rc.pretty(item.locations, 'comma_list') }}
            </template>

            <template
                v-if="$rc.$state.isAuthActorSystemAdmin()"
                #header.number_reassignment_check_since_date
            >
                <m-restricted-table-header-cell text="Number Reassignment Check Since Date" />
            </template>
            <template #item.number_reassignment_check_since_date="{ item }">
                {{ $rc.pretty(item.number_reassignment_check_since_date, 'date') }}
            </template>

            <template #item.custom_message="{ item }">
                {{ prettyCustomMessage(item.custom_message) }}
            </template>

            <template #item.status="{ item }">
                {{ prettyStoredMessageState(item) }}
            </template>

            <template #item.action="{ item }">
                <v-menu
                    v-if="getActionMenuItems(item).length > 0"
                    bottom
                    offset-y
                >
                    <template #activator="{ on, attrs }">
                        <v-icon
                            v-bind="attrs"
                            v-on="on"
                        >
                            mdi-dots-vertical-circle-outline
                        </v-icon>
                    </template>

                    <v-list dense>
                        <template v-for="(action, index) in getActionMenuItems(item)">
                            <v-divider
                                v-if="action.divider"
                                :key="'divider-' + index"
                            />
                            <m-restricted-menu-list-item
                                v-else-if="action.restricted"
                                :key="'restricted-' + action.key"
                                :text="action.text"
                                @click="action.handler(item)"
                            />
                            <v-list-item
                                v-else
                                :key="'item-' + action.key"
                                @click="action.handler(item)"
                            >
                                <v-list-item-title>{{ action.text }}</v-list-item-title>
                            </v-list-item>
                        </template>
                    </v-list>
                </v-menu>
            </template>
        </v-data-table>

        <p class="text-caption text--secondary mt-2">* -- State and Timezone are computed on a "best effort" basis based
            on POS / CRM profiles, area code and other data points. Location may be missing or inaccurate.
        </p>

        <div class="mt-4 text-right">
            <v-tooltip bottom>
                <template #activator="{ on, attrs }">
                    <v-btn
                        class="mx-2"
                        fab
                        dark
                        small
                        color="primary"
                        v-bind="attrs"
                        v-on="on"
                        @click="onClickCreateRecipient"
                    >
                        <v-icon dark>mdi-plus</v-icon>
                    </v-btn>
                </template>
                Add one recipient
            </v-tooltip>

            <v-tooltip bottom>
                <template #activator="{ on, attrs }">
                    <v-btn
                        class="mx-2"
                        fab
                        dark
                        small
                        color="primary"
                        v-bind="attrs"
                        v-on="on"
                        @click="onClickAddContacts"
                    >
                        <v-icon dark>mdi-account-multiple-plus</v-icon>
                    </v-btn>
                </template>
                Add multiple recipients (using contacts)
            </v-tooltip>

            <v-tooltip bottom>
                <template #activator="{ on, attrs }">
                    <v-btn
                        class="mx-2"
                        fab
                        dark
                        small
                        color="primary"
                        v-bind="attrs"
                        v-on="on"
                        @click="onClickExcelImport"
                    >
                        <v-icon dark>mdi-microsoft-excel</v-icon>
                    </v-btn>
                </template>
                Add recipients from an Excel file
            </v-tooltip>

            <!-- The add recipients from a list is not a menu -->
            <v-tooltip
                v-if="!show_add_recipients_from_list_menu"
                bottom
            >
                <template #activator="{ on, attrs }">
                    <v-btn
                        class="mx-2"
                        fab
                        dark
                        small
                        color="primary"
                        v-bind="attrs"
                        v-on="on"
                        @click="onClickAddAllListMembers"
                    >
                        <v-icon dark>mdi-format-list-bulleted-square</v-icon>
                    </v-btn>
                </template>
                Add recipients from a list
            </v-tooltip>

            <!-- The add recipients from a list is not a menu -->
            <v-menu
                v-if="show_add_recipients_from_list_menu"
                bottom
                offset-y
            >
                <template #activator="{ on }">
                    <v-btn
                        class="mx-2"
                        fab
                        dark
                        small
                        color="primary"
                        v-on="on"
                    >
                        <v-icon dark>mdi-format-list-bulleted-square</v-icon>
                    </v-btn>
                </template>

                <v-list>
                    <v-list-item
                        v-for="(item, index) in add_recipients_from_list_menu_items"
                        :key="'ram_' + index"
                        @click="item.action"
                    >
                        <v-list-item-content>
                            <v-list-item-title>
                                {{ item.text }}

                                <m-menu-chip v-if="item.badge_new" />
                            </v-list-item-title>
                        </v-list-item-content>
                    </v-list-item>
                </v-list>
            </v-menu>

            <v-menu
                bottom
                offset-y
            >
                <template #activator="{ on }">
                    <v-btn
                        class="mx-2"
                        fab
                        dark
                        small
                        color="primary"
                        v-on="on"
                    >
                        <v-icon dark>mdi-dots-vertical</v-icon>
                    </v-btn>
                </template>

                <v-list>
                    <v-list-item
                        v-if="$rc.inIndustry('wine')"
                        @click="onClickStartImportWinePulse()"
                    >
                        <v-list-item-title>Add recipients from WinePulse report...</v-list-item-title>
                    </v-list-item>

                    <v-divider v-if="$rc.inIndustry('wine')" />

                    <template v-if="$rc.$state.isAuthActorSystemAdmin() && bulk_message.is_demo_organization">
                        <m-restricted-menu-list-item
                            text="Add dummy recipients..."
                            @click="onClickAddDummyRecipients()"
                        />

                        <m-restricted-menu-list-item
                            text="Add dummy (spam) recipients..."
                            @click="onClickAddDummySpamRecipients()"
                        />

                        <v-divider />
                    </template>

                    <template v-if="$rc.$state.isCommerce7Enabled()">
                        <v-list-item @click="onClickRemoveRecipientsFromRecentOrders">
                            <v-list-item-title>Remove recipients that placed a recent order...</v-list-item-title>
                            <m-menu-chip />
                        </v-list-item>

                        <m-restricted-menu-list-item
                            v-if="$rc.$state.isAuthActorSystemAdmin()"
                            text="Add recipients who have not customized their club shipment..."
                            @click="onClickAddNotCustomClubShipment"
                        >
                            <m-menu-chip />
                        </m-restricted-menu-list-item>

                        <v-divider />
                    </template>

                    <template v-if="bulk_message.is_sentiment_forecast_available">
                        <v-list-item @click="onClickOptimizeRecipients(false)">
                            <v-list-item-title>RedChirp AI: Optimize Recipients...</v-list-item-title>
                            <m-menu-chip />
                        </v-list-item>

                        <v-divider />
                    </template>

                    <v-list-item @click="$emit('edit-error-rate-before-cancel')">
                        <v-list-item-title>Adjust max error rate...</v-list-item-title>
                    </v-list-item>

                    <v-list-item @click="deleteAllRecipients">
                        <v-list-item-title>Delete all recipients...</v-list-item-title>
                    </v-list-item>
                </v-list>
            </v-menu>
        </div>

        <!-- Dialogs -->
        <m-bulk-recipient-edit-dialog
            ref="edit-bulk-recipient"
            @ok="$emit('update-recipients')"
        />

        <m-autocomplete-phone-number
            ref="add-phone-numbers"
            action_button_text="Add Recipients"
            dialog_only
            default_multiselect
            @start-group-text="onAddMultiplePhoneNumbers"
        />

        <m-edit-select-dialog
            ref="select-list"
            multiple
            @ok="onAddAllListMembers($event)"
        />

        <m-bulk-message-add-recipients-yet-to-customize-club-dialog
            ref="m-bulk-message-add-recipients-yet-to-customize-club-dialog"
            @ok="onOkAddRecipientsYetToCustomizeClubDialog"
        />

        <m-bulk-recipient-custom-message-dialog
            ref="custom-message-dialog"
            hint="The custom message will override the bulk message text for this recipient only."
            @ok="updateCustomMessageForRecipient"
        />

        <m-edit-blobs-dialog
            ref="import-wine-pulse-report-dialog"
            :externally_visible="false"
            :accept="['csv']"
            :allow_multiple="false"
            purpose="excel-import"
            title="Import WinePulse Report"
            winepulse
            @ok="onImportWinePulseReport"
        />

        <m-model-r1-optimize-recipients-dialog
            ref="m-model-r1-optimize-recipients-dialog"
            @ok="$emit('update-recipients')"
        />
    </div>
</template>

<script>
import { v4 as uuidv4 } from 'uuid';
import Time from '@/js/time.js';
const _ = require('lodash');

export default {
    name: 'BulkRecipientsTable',

    props: {
        organization_id: {
            type: String,
            required: true,
        },
        bulk_message: {
            type: Object,
            required: false,
            default: null
        },
        bulk_message_id: {
            type: String,
            required: true,
        },
        loading_recipients: {
            type: Boolean,
            required: true,
        },
        full_text: {
            type: String,
            required: false,
            default: '',
        },
        recipients: {
            type: Array,
            required: true,
        },
        recipient_extra_fields: {
            type: Array,
            required: false,
            default: () => [],
        },
        num_recipients_under_current_filter: {
            type: Number,
            required: true,
        },
        table_options: {
            type: Object,
            required: false,
            default: null,
        },
    },

    data() {
        return {};
    },

    computed: {
        local_table_options: {
            get() {
                return this.table_options;
            },
            set(value) {
                this.$emit('update:table_options', value);
            }
        },

        recipient_headers() {
            const ret = [];

            ret.push({ text: 'First', value: 'recipient_contact_first_name' });
            ret.push({ text: 'Last', value: 'recipient_contact_last_name' });
            ret.push({ text: 'Phone Number', value: 'recipient_endpoint' });
            ret.push({ text: 'Location*', value: 'locations' });

            if (this.$rc.$state.isAuthActorSystemAdmin()) {
                ret.push({ text: 'Number Reassignment Check Since Date', value: 'number_reassignment_check_since_date' });
            }

            if (this.recipient_extra_fields) {
                for (let i = 0; i < this.recipient_extra_fields.length; i++) {
                    let extra_field = {
                        text: this.recipient_extra_fields[i].extra_field_key,
                        value: 'extra_field_' + this.recipient_extra_fields[i].extra_field_key,
                        'sortable': false,
                    };

                    ret.push(extra_field);
                }
            }

            ret.push({ text: 'Custom Message', value: 'custom_message' });

            ret.push({ text: '', value: 'action', 'sortable': false });

            return ret;
        },

        add_recipients_from_list_menu_items() {
            let ret = [];

            ret.push({ text: 'Add recipients from a list...', action: this.onClickAddAllListMembers });

            if (this.$rc.$state.isCRMEnabled('commerce7')) {
                ret.push({ text: 'Add recipients using a Commerce7 tag...', action: this.onClickAddCommerce7Tag });

                ret.push({ text: 'Add recipients that are active club members in Commerce7...', action: this.onClickAddCommerce7Club });
                ret.push({ text: 'Add recipients who have not customized their club shipment...', action: this.onClickAddNotCustomClubShipment, badge_new: true });

                ret.push({ text: 'Add guests from Commerce7 reservations...', action: () => this.onClickAddRecipientsUsingReservations('commerce7') });
            }

            if (this.$rc.$state.isCRMEnabled('tock-v2')) {
                ret.push({ text: 'Add guests from Tock reservations...', action: () => this.onClickAddRecipientsUsingReservations('tock-v2') });
            }

            if (this.$rc.$state.isCRMEnabled('ecellar')) {
                ret.push({ text: 'Add guests from eCellar reservations...', action: () => this.onClickAddRecipientsUsingReservations('ecellar') });
            }

            if (this.$rc.$state.isCRMEnabled('order-port')) {
                ret.push({ text: 'Add guests from OrderPort reservations...', action: () => this.onClickAddRecipientsUsingReservations('order-port') });
            }

            if (this.$rc.$state.isCRMEnabled('offset')) {
                ret.push({ text: 'Add recipients from Offset groups...', action: this.onClickAddOffsetGroup, badge_new: true });
                //Hide for now
                //ret.push({text: 'Add club members from Offset...', action: this.onClickAddOffsetClub, badge_new: true});
            }

            if (this.$rc.$state.isCRMEnabled('vinoshipper')) {
                ret.push({text: 'Add recipients that are active club members in Vinoshipper...', action: this.onClickAddVinoshipperClub});
            }

            return ret;
        },

        show_add_recipients_from_list_menu() {
            if (this.$rc.$state.isCRMEnabled('commerce7')) return true;
            if (this.$rc.$state.isCRMEnabled('tock-v2')) return true;
            if (this.$rc.$state.isCRMEnabled('ecellar')) return true;
            if (this.$rc.$state.isCRMEnabled('offset')) return true;
            if (this.$rc.$state.isCRMEnabled('vinoshipper')) return true;

            return false;
        },
    },

    methods: {
        onSearchInputDebounced: _.debounce(function (value) {
            this.$emit('update-full-text', value);
        }, 751),

        genericCompare(first, second, desc) {
            // nulls should be last
            if (first == null && second != null) return 1;
            if (first != null && second == null) return -1;
            if (first == null && second == null) return 0;

            if (!desc) {
                return first > second ? 1 : -1;
            } else {
                return second > first ? 1 : -1;
            }
        },

        customRecipientSort(items, headers, is_desc) {
            // descending information comes in through an array
            const desc = is_desc[0] ? true : false;

            // we don't support multi column, sort, so simply sort on the first header
            const index_header = headers[0];
            if (!index_header) return items;

            items.sort((a, b) => {
                if (index_header === "sent_time") {
                    return this.genericCompare(a.sent_time, b.sent_time, desc);
                }
                if (index_header === "pretty_recipient_endpoint") {
                    return this.genericCompare(a.recipient_endpoint, b.recipient_endpoint, desc);
                }
                if (index_header === "status") {
                    return this.genericCompare(a.state, b.state, desc);
                }
                if (index_header === "replied") {
                    return this.genericCompare(a.replied, b.replied, desc);
                }
                else {
                    return this.genericCompare(a[index_header], b[index_header], desc);
                }
            });

            return items;
        },

        onClickUpdateRecipientLocations() {
            this.$rc.rest({
                spinner: false,
                endpoint: 'PUT: /api/v1/bulk/compute-missing-location',

                params: {
                    bulk_message_id: this.bulk_message_id,
                    recompute_all: true,
                },

                on_success: (data) => {
                    this.$rc.alert('Updating Recipient Location', 'The process of updating the location for all recipients has been started. This may take several minutes.<br/><br/>You can use the Refresh menu item to view the results as they are obtained.');
                },

                on_error: 'Unable to compute locations',
            });
        },

        onClickUpdateNumberReassignmentCheckSinceDates() {
            this.$rc.rest({
                spinner: false,
                endpoint: 'PUT: /api/v1/bulk/compute-missing-number-reassignment-check-since-dates',

                params: {
                    bulk_message_id: this.bulk_message_id,
                    recompute_all: true,
                },

                on_success: (data) => {
                    this.$rc.alert('Updating number reassignment check since dates', 'The process of updating the number reassignment check since dates for all recipients has been started.  This may take several minutes.<br/><br/>You can use the Refresh menu item to view the results as they are obtained.');
                },

                on_error: 'Unable to compute number reassignment check since dates',
            });
        },

        onClickCreateRecipient() {
            this.$refs['edit-bulk-recipient'].showDialog(
                this.organization_id,
                this.bulk_message_id,
                null
            );
        },

        onClickAddContacts() {
            this.$refs['add-phone-numbers'].showDialog();
        },

        onAddMultiplePhoneNumbers(items) {
            const endpoints = [];

            for (let i = 0; i < items.length; i++) {
                endpoints.push(items[i].value);
            }

            const params = {
                bulk_message_id: this.bulk_message_id,
                endpoints
            };

            this.$rc.showSpinner();

            this.$rc.rest(
                'POST: /api/v1/bulk/recipients/put-contacts',
                params,

                (data) => {
                    this.$rc.hideSpinner();
                    this.$emit('update-recipients');
                },

                () => {
                    this.$rc.hideSpinner();
                    this.$rc.alert('Error', 'Unable to add recipients');
                },
            );
        },

        onClickExcelImport() {
            const import_id = uuidv4();

            this.$router.push({
                name: 'bulk-recipient-import',
                params: {
                    organization_id: this.organization_id,
                    bulk_message_id: this.bulk_message_id,
                    import_id,
                }
            }).catch(e => { }); // swallow any exception
        },

        onClickAddAllListMembers() {
            this.$rc.showSpinner();

            this.$rc.rest(
                'LIST: /api/v1/lists/choose-org-list',

                {
                    organization_id: this.organization_id,
                    include_auto_lists: true
                },

                (data) => {
                    this.$rc.hideSpinner();
                    this.$refs['select-list'].showDialog('Select List', null, data.results);
                },

                () => {
                    this.$rc.hideSpinner();
                    this.$rc.alert('Error', 'Unable to complete API call');
                },
            );
        },

        onAddAllListMembers(list) {
            this.$rc.rest({
                spinner: true,
                endpoint: 'PUT: /api/v1/bulk/recipients/list-to-preview',

                params: {
                    list_id: list,
                    bulk_message_id: this.bulk_message_id,
                    rest_timeout: 60000,
                },

                on_success: (data) => {
                    this.$rc.hideSpinner();

                    this.$router.push({
                        name: 'bulk-recipient-preview',
                        params: {
                            organization_id: data.organization_id,
                            bulk_message_id: data.bulk_message_id,
                            import_id: data.import_id
                        }
                    });
                },

                on_error: 'Unable to add list members to recipients',
            });
        },

        onClickAddCommerce7Tag() {
            this.$router.push({
                name: 'crm-tag-meta-data',
                params: {
                    organization_id: this.organization_id,
                    content_type: 'commerce7',
                },
                query: {
                    add_to_bulk_message_id: this.bulk_message_id,
                },
            });
        },

        onClickAddCommerce7Club() {
            this.onClickAddFromClub('commerce7');
        },

        onClickAddVinoshipperClub() {
            this.onClickAddFromClub('vinoshipper');
        },
        
        onClickAddFromClub(content_type) {
            this.$router.push({
                name: 'crm-club-meta-data',
                params: {
                    organization_id: this.$route.params.organization_id,
                    content_type,
                },
                query: {
                    add_to_bulk_message_id: this.$route.params.bulk_message_id,
                },
            });
        },

        onClickAddNotCustomClubShipment() {
            this.$refs['m-bulk-message-add-recipients-yet-to-customize-club-dialog'].showDialog();
        },

        onOkAddRecipientsYetToCustomizeClubDialog(club_release) {
            /*this.$rc.rest({
                'spinner': false,
                'endpoint': 'PUT: /api/v1/bulk/add-dummy-recipients',
    
                'params' : {
                        'size': 723,
                        'bulk_message_id': this.bulk_message_id,
                    },
    
                'on_success': (data) => {
                        this.$emit('update');
                    },
    
                'on_error': 'Unable to add club members that have not customized this shipment',
            }); */

            this.$rc.startProcess({
                type: 'crm-create-excel-preview-of-customers-who-have-not-customized-club-release',

                params: {
                    organization_id: this.organization_id,
                    bulk_message_id: this.bulk_message_id,
                    ...club_release
                },

                title: 'Listing customers who have not customized club release',
                finished_button_text: 'Preview Recipients',
                failed_button_text: 'Cancel',

                status: (pte) => {
                    return 'Customer phone numbers: ' + pte.progress_counter;
                },

                on_finished: (pte) => {
                    this.$router.push({
                        name: 'bulk-recipient-preview',
                        params: {
                            organization_id: pte.output.organization_id,
                            bulk_message_id: pte.output.bulk_message_id,
                            import_id: pte.output.import_id
                        }
                    });
                },

                on_failed: (pte) => {
                },
            });
        },

        onClickAddRecipientsUsingReservations(content_type) {
            this.$router.push({
                name: 'crm-reservations',
                params: {
                    organization_id: this.organization_id,
                    content_type,
                },
                query: {
                    add_to_bulk_message_id: this.bulk_message_id,
                },
            });
        },

        onClickAddOffsetGroup() {
            this.$router.push({
                name: 'crm-group-meta-data',
                params: {
                    organization_id: this.organization_id,
                    integration_type: 'offset',
                },
                query: {
                    add_to_bulk_message_id: this.bulk_message_id,
                },
            });
        },

        onClickAddOffsetClub() {
            this.$router.push({
                name: 'crm-club-meta-data',
                params: {
                    organization_id: this.organization_id,
                    content_type: 'offset',
                },
                query: {
                    add_to_bulk_message_id: this.bulk_message_id,
                },
            });
        },

        promptForDummyRecipientCount(after_action) {
            this.$rc.prompt({
                title: 'Number of recipients',
                label: 'Number of recipients',
                value: '300',
                after_prompt: (value) => {
                    const size = parseInt(value);
                    if (Number.isNaN(size)) return;
                    after_action(size);
                }
            });
        },

        onClickAddDummyRecipients() {
            this.promptForDummyRecipientCount(
                (size) => {
                    this.$rc.rest({
                        spinner: false,
                        endpoint: 'PUT: /api/v1/bulk/add-dummy-recipients',

                        params: {
                            size,
                            bulk_message_id: this.bulk_message_id,
                        },

                        on_success: (data) => {
                            this.$emit('update');
                        },

                        on_error: 'Unable to add dummy recipients',
                    });


                }
            );
        },

        onClickAddDummySpamRecipients() {
            this.promptForDummyRecipientCount(
                (size) => {
                    this.$rc.rest({
                        spinner: false,
                        endpoint: 'PUT: /api/v1/bulk/add-dummy-recipients',

                        params: {
                            size,
                            bulk_message_id: this.bulk_message_id,
                            number_type: 'spam'
                        },

                        on_success: (data) => {
                            this.$emit('update');
                        },
                        on_error: 'Unable to add dummy recipients',
                    });
                }
            );
        },

        onClickDummyExport() {
            this.promptForDummyRecipientCount(
                (size) => {
                    this.$rc.excelExport(
                        'EXPORT: /api/v1/bulk/dummy-recipients',
                        { size }
                    );
                }
            );
        },

        onClickWinePulseDummy() {
            window.open('https://files.redchirp.com/ecwqreeducufuzsybbbcxtedvfyuwvse.csv', '_blank');
        },

        deleteAllRecipients() {
            this.$rc.confirm(
                'Delete All Recipients',
                'Are you sure you want to delete all recipients? This action cannot be undone.',
                [
                    { text: 'Cancel', value: 'cancel' },
                    { text: 'Confirm', value: 'confirm' },
                ],
                (response) => {
                    if (response === 'confirm') {
                        this.$rc.rest({
                            spinner: false,
                            endpoint: 'DELETE: /api/v1/bulk/recipients/all',

                            params: {
                                bulk_message_id: this.bulk_message_id
                            },

                            on_success: (data) => {
                                this.$emit('update-recipients');
                            },
                            on_error: 'There was an error deleting the recipients, please try again.',
                        });
                    }
                    else {
                        return;
                    }
                }
            );
        },

        prettyFirstName(item) {
            if (item.recipient_contact_first_name != null)
                return item.recipient_contact_first_name;

            if (item.extra_fields.first_name != null)
                return item.extra_fields.first_name;

            return null;
        },

        prettyLastName(item) {
            if (item.recipient_contact_last_name != null)
                return item.recipient_contact_last_name;

            if (item.extra_fields.last_name != null)
                return item.extra_fields.last_name;

            return null;
        },

        prettyStoredMessageState(item) {
            if (item.opt_out_state == 'opt-out') {
                return 'Opted Out';
            }

            if (item.state == 'cancelled') {
                return 'Cancelled';
            }

            let state = item.state;

            // edit state checked for backwards compatibility
            if (!state || ['pending', 'edit'].includes(state)) return '';

            if (state == 'sent') {
                state = item.stored_message_state;

                if (state == null || state == '') return '';

                if (state == 'delivered') return 'Delivered';
                if (state == 'in-transit') return 'In Transit';
                if (state == 'error') return 'Error';
            }

            if (state == 'filtered') {
                return 'Filtered';
            }

            return 'Failed';
        },

        prettyCustomMessage(custom_message) {
            if (custom_message == null || custom_message == '') return 'No';
            return 'Yes';
        },

        editCustomMessage(recipient) {
            this.$refs['custom-message-dialog'].showDialog(recipient.bulk_message_recipient_id, recipient.custom_message);
        },

        updateCustomMessageForRecipient(recipient_id, new_message) {
            if (new_message) new_message = new_message.trim();

            this.$rc.updateBulkRecipientCustomMessage(
                recipient_id,
                new_message,
                (data) => {
                    this.$emit('update-recipients');
                },
            );
        },

        onClickEditRecipient(recipient) {
            this.$refs['edit-bulk-recipient'].showDialog(
                this.organization_id,
                this.bulk_message_id,
                recipient
            );
        },

        onClickLocationDetails(item) {
            this.$rc.rest({
                spinner: true,
                endpoint: 'GET: /api/v1/bulk/location/:bulk_message_recipient_id',

                params: {
                    bulk_message_recipient_id: item.bulk_message_recipient_id,
                },

                on_success: (data) => {
                    if (data.technical_details == null) {
                        this.$rc.alert('No location', 'This recipient does not have a location.  Use the "Update recipient locations..." menu item to update locations for all recipients');
                    }
                    else {
                        this.$rc.technicalDetails({
                            title: 'Location Technical Details',
                            technical_details: data.technical_details,
                        });
                    }
                },

                on_error: 'Unable to complete API call',
            });
        },

        onClickNumberReassignmentCheckSinceDateDetails(item) {
            this.$rc.rest({
                spinner: true,
                endpoint: 'GET: /api/v1/bulk/number-reassignment-check-since-date/:bulk_message_recipient_id',

                params: {
                    bulk_message_recipient_id: item.bulk_message_recipient_id,
                },

                on_success: (data) => {
                    if (data.technical_details == null) {
                        this.$rc.alert('No number reassignment check since date', 'This recipient does not have a number reassignment check since date.  Use the "Update number reassignment check since dates..." menu item to update all recipients');
                    }
                    else {
                        this.$rc.technicalDetails({
                            title: 'Number Reassignment Check Since Date Technical Details',
                            technical_details: data.technical_details,
                        });
                    }
                },

                on_error: 'Unable to complete API call',
            });
        },

        deleteRecipient(recipient) {
            this.$emit('delete-recipient', recipient);
        },

        getActionMenuItems(item) {
            const actions = [];

            actions.push({
                key: 'preview-message',
                text: 'Preview Message',
                handler: (item) => this.$emit('preview-message', item),
            });

            actions.push({
                key: 'send-test',
                text: 'Send Test',
                handler: (item) => this.$emit('select-test-message-recipient', item),
            });

            actions.push({
                key: 'custom-message',
                text: 'Custom Message',
                handler: this.editCustomMessage,
            });

            actions.push({
                key: 'edit-recipient',
                text: 'Edit Recipient',
                handler: this.onClickEditRecipient,
            });

            actions.push({
                key: 'location-details',
                text: 'Location technical details',
                handler: this.onClickLocationDetails,
            });

            if (this.$rc.$state.isAuthActorSystemAdmin()) {
                actions.push({
                    key: 'number-reassignment-details',
                    text: 'Number reassignment check since date technical details...',
                    handler: this.onClickNumberReassignmentCheckSinceDateDetails,
                    restricted: true,
                });
            }

            actions.push({ divider: true });

            actions.push({
                key: 'delete-recipient',
                text: 'Delete Recipient',
                handler: this.deleteRecipient,
            });

            return actions;
        },

        onClickStartImportWinePulse() {
            this.$refs['import-wine-pulse-report-dialog'].showDialog();
        },

        onImportWinePulseReport(blobs) {
            if (blobs.length == 0) return;

            const blob_id = blobs[0];

            this.$rc.rest({
                spinner: true,
                endpoint: 'PUT: /api/v1/bulk/recipients/wine-pulse-import',

                params: {
                    bulk_message_id: this.bulk_message_id,
                    blob_id,
                },

                on_success: (data) => {
                    this.$router.push({
                        name: 'bulk-recipient-preview',
                        params: {
                            organization_id: data.organization_id,
                            bulk_message_id: data.bulk_message_id,
                            import_id: data.import_id
                        }
                    });
                },

                on_error: 'Unable to import WinePulse report',
            });
        },

        onClickRemoveRecipientsFromRecentOrders() {
            this.$router.push({
                name: 'bulk-recipient-remove-recent-order',
                params: {
                    organization_id: this.organization_id,
                    bulk_message_id: this.bulk_message_id,
                }
            }).catch(e => { }); // swallow any exception
        },

        onClickOptimizeRecipients(read_only) {
            this.$refs['m-model-r1-optimize-recipients-dialog'].showDialog(this.bulk_message.recipient_count, read_only);
        },
    },
}
</script>